import { CheckSquare, Square } from "lucide-react";
import {
  formatINR,
  sqft,
  formatDims,
  fileUrl,
  roomSqftBreakdown,
  roomsSqftBreakdown,
  formatSqftBreakdown,
  splitSqftBreakdown,
} from "@/lib/quotationApi";

export default function PublicRooms({ effectiveQ, tierList, sortedTierList, excluded, snapshotMode, toggleItem, primary }) {
  // Iterate tiers in descending order for per-room columns. Fallback to
  // configured order if a sorted list wasn't supplied.
  const iterList = sortedTierList || tierList;
  return (
    <>
      <div className="card-luxe mb-4">
        <div className="overline text-white/50 mb-4">Scope of work</div>
        {(effectiveQ.rooms || []).map((room, ri) => {
          const tierRoomCost = (tierId) => {
            const t = tierList.find((x) => x.id === tierId);
            if (!t) return 0;
            return (t.breakdown.rooms || []).find((r) => r.id === room.id)?.area_cost || 0;
          };
          return (
            <div key={room.id} data-pdf-block className="mb-5 last:mb-0 pb-4 last:pb-0 border-b border-white/5 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-semibold text-[15px]">
                  {room.area_type}
                  {room.description && (
                    <span className="text-white/40 font-normal text-sm"> · {room.description}</span>
                  )}
                </div>
              </div>

              {room.images && room.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {room.images.map((img) => (
                    <img
                      key={img.id}
                      src={fileUrl(img.storage_path)}
                      alt=""
                      className="w-full aspect-square rounded-lg object-cover border border-white/10"
                    />
                  ))}
                </div>
              )}

              <div className="space-y-1.5 mb-3">
                {(room.measurements || []).map((m) => {
                  const area = sqft(m);
                  const isEx = excluded.has(m.id);
                  return (
                    <div
                      key={m.id}
                      className={`flex justify-between items-start gap-2 text-xs pl-3 border-l py-1 ${isEx ? "border-white/10" : "border-[#C5A880]/30"}`}
                      data-testid={`public-measurement-row-${m.id}`}
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        {!snapshotMode && (
                          <button
                            type="button"
                            onClick={() => toggleItem(m.id)}
                            className="mt-0.5 shrink-0 transition-opacity hover:opacity-80"
                            style={{ color: isEx ? "rgba(255,255,255,0.35)" : primary }}
                            aria-label={isEx ? "Include item" : "Exclude item"}
                            data-testid={`public-toggle-measurement-${m.id}`}
                          >
                            {isEx ? <Square size={15} /> : <CheckSquare size={15} />}
                          </button>
                        )}
                        <div className={isEx ? "opacity-50" : "text-white/70"}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isEx ? (
                              <div className="pdf-strike w-full">{formatDims(m)} · {m.finish_name || "—"}</div>
                            ) : (
                              <>
                                <span>{formatDims(m)} · {m.finish_name || "—"}</span>
                                {m.is_modular && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#C5A880]/50 text-[#C5A880] uppercase tracking-wider">
                                    Modular
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {m.description && (
                            <div className={`text-white/40 ${isEx ? "pdf-strike" : ""}`}>{m.description}</div>
                          )}
                        </div>
                      </div>
                      <div
                        className={`text-right whitespace-nowrap ${isEx ? "pdf-strike opacity-40 text-white/40" : "text-white/50"}`}
                      >
                        {area.toFixed(1)} sqft
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-room sqft breakdown */}
              <div
                className="flex justify-between items-center text-[12px] pl-3 pr-1 mb-3"
                data-testid={`public-room-sqft-${ri}`}
              >
                <span className="text-white/45 uppercase tracking-wider text-[10px]">
                  Total area
                </span>
                <span className="text-white/85 font-medium">
                  {formatSqftBreakdown(roomSqftBreakdown(room))}
                </span>
              </div>

              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(1, iterList.length)}, minmax(0, 1fr))`,
                }}
                data-testid={`public-room-tiers-${ri}`}
              >
                {iterList.map((t) => {
                  return (
                    <div
                      key={t.id}
                      className="rounded-lg border border-white/5 bg-white/[0.02] p-2"
                    >
                      <div
                        className="text-[9px] uppercase tracking-wider truncate"
                        style={{ color: t.color }}
                      >
                        {t.name}
                      </div>
                      <div
                        className="font-display font-semibold text-[13px] mt-0.5"
                        style={{ color: t.color }}
                      >
                        {formatINR(tierRoomCost(t.id))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Grand total sqft across all rooms */}
      {(effectiveQ.rooms || []).length > 0 && (() => {
        const gs = splitSqftBreakdown(roomsSqftBreakdown(effectiveQ.rooms));
        return (
          <div
            className="card-luxe !py-3 mb-4 flex justify-between items-center gap-3"
            data-testid="public-grand-sqft"
          >
            <span className="overline text-white/50">Grand total area</span>
            <span className="text-right">
              <span className="font-display font-semibold text-[15px] text-[#C5A880]">
                {gs.main}
              </span>
              {gs.detail && (
                <span className="font-display text-[12px] text-white/45 ml-1.5">
                  {gs.detail}
                </span>
              )}
            </span>
          </div>
        );
      })()}
    </>
  );
}
